'use client'

import Link from 'next/link'
import React from 'react'

import {
  PolaroidGalleryCarousel,
  type PolaroidGalleryItem,
} from '@/components/PolaroidGalleryCarousel'
import { Button } from '@/components/ui/button'
import { ColorTrailText } from '@/components/ui/color-trail-text'
import { WhyNeonWoodSection } from '@/components/WhyNeonWood'
import { useMessages } from '@/lib/i18n/useMessages'
import { getInspectionCalendarHref } from '@/lib/pages/inspectionCalendarLink'
import { useTemplateHref } from '@/lib/pages/LocalizedPagePathsProvider'

const TUCAN_IMAGE = '/media/homepage/tucan.webp'

const featureStyles = [
  { iconBg: 'bg-[#FFD7B0]', iconColor: 'text-[#C46A1B]' },
  { iconBg: 'bg-[#B4F0DD]', iconColor: 'text-dark-green' },
  { iconBg: 'bg-[#B0D9FF]', iconColor: 'text-bluetext' },
  { iconBg: 'bg-[#CCF7FF]', iconColor: 'text-bluetext' },
] as const

type WhyNeonWoodProps = {
  polaroidGalleryItems: PolaroidGalleryItem[]
}

export const WhyNeonWood: React.FC<WhyNeonWoodProps> = ({ polaroidGalleryItems }) => {
  const messages = useMessages()
  const copy = messages.landing.whyNeonWood
  const tourHref = getInspectionCalendarHref()
  const apartmentsHref = useTemplateHref('apartments')
  const showPolaroidGallery = polaroidGalleryItems.length > 0

  return (
    <WhyNeonWoodSection
      actions={
        <>
          <Button asChild>
            <Link href={tourHref} rel="noopener noreferrer" target="_blank">
              {copy.watchTour}
              <span className="sr-only"> {messages.common.opensInNewTab}</span>
            </Link>
          </Button>
          <Button asChild variant="outline">
            <Link href={apartmentsHref}>{copy.exploreApartments}</Link>
          </Button>
        </>
      }
      after={
        showPolaroidGallery ? (
          <PolaroidGalleryCarousel
            ariaLabel={copy.polaroidGalleryAriaLabel}
            autoplayPauseLabel={copy.polaroidAutoplayPauseLabel}
            autoplayPlayLabel={copy.polaroidAutoplayPlayLabel}
            items={polaroidGalleryItems}
            detailDialog={{
              closeLabel: copy.polaroidDetailCloseLabel,
            }}
            nextLabel={copy.polaroidScrollNext}
            playLabel={copy.playVideo}
            previousLabel={copy.polaroidScrollPrevious}
          />
        ) : null
      }
      decorativeImage={{
        className: 'relative z-10 h-auto w-44 lg:-mb-12',
        height: 208,
        src: TUCAN_IMAGE,
        width: 208,
      }}
      features={copy.features.map((feature, index) => ({
        key: feature.title,
        description: feature.description,
        iconBg: featureStyles[index].iconBg,
        iconColor: featureStyles[index].iconColor,
        title: feature.title,
      }))}
      headingId="why-neon-wood-heading"
      sectionClassName="relative isolate"
      stats={{
        ariaLabel: copy.statsAriaLabel,
        nationalitiesLabel: copy.nationalitiesLabel,
        nationalitiesValue: copy.nationalitiesValue,
        ratingsLabel: copy.ratingsLabel,
        ratingsValue: copy.ratingsValue,
      }}
      title={
        <ColorTrailText
          as="h2"
          className="text-title min-w-0 max-w-[46.0625rem] font-semibold tracking-[-0.04em] text-bluetext lg:text-balance"
          id="why-neon-wood-heading"
          segments={[
            {
              finalColor: 'var(--brand-bluetext)',
              text: copy.title,
            },
          ]}
        />
      }
    />
  )
}
